import { Controller } from "@hotwired/stimulus"
import operationalStatusChannel from "../channels/operational_status_channel"
import * as Turbo from "@hotwired/turbo"

/**
 * Contrôleur pour la gestion temps réel des cabines UV
 * 
 * Architecture:
 * - Utilise un channel Action Cable partagé (singleton) pour toutes les cabines
 * - Le serveur Rails proxyfie les connexions vers les Raspberry Pi
 */
export default class extends Controller {
  static targets = ["timer", "timerLabel", "statusBadge", "counterDisplay", "timerContainer", "prepareButton", "cleaningButton", "testButton", "actionsContainer", "prepDurationControls", "prepDurationDisplay", "emergencyStopButton"]
  static values = {
    cabineId: Number,
    appointmentId: Number,
    remainingSeconds: Number,
    status: String,
    raspberryIp: String,
    relayPin: Number,
    prepDuration: Number,
    isTest: { type: Boolean, default: false }
  }

  // Mapping des couleurs par statut (pour la carte)
  static cardColors = {
    loading: "bg-gray-50 border-gray-300",
    available: "bg-white border-gray-200",
    preparing: "bg-blue-200 border-blue-500",
    in_progress: "bg-green-200 border-green-500",
    testing: "bg-orange-200 border-orange-500",
    cooling_down: "bg-yellow-200 border-yellow-500",
    needs_cleaning: "bg-red-200 border-red-500",
    error: "bg-gray-100 border-gray-300"
  }

  // Mapping des couleurs et labels pour le badge
  static statusConfig = {
    loading: { color: 'bg-gray-200 text-gray-600', label: 'Connexion...' },
    available: { color: 'bg-gray-100 text-gray-800', label: 'Disponible' },
    preparing: { color: 'bg-blue-200 text-blue-800', label: 'En préparation' },
    in_progress: { color: 'bg-green-200 text-green-800', label: 'En cours' },
    testing: { color: 'bg-orange-200 text-orange-800', label: 'Test' },
    cooling_down: { color: 'bg-yellow-200 text-yellow-800', label: 'Ventilation' },
    needs_cleaning: { color: 'bg-red-200 text-red-800', label: 'À nettoyer' },
    error: { color: 'bg-red-100 text-red-800', label: 'Erreur connexion' }
  }

  connect() {
    // Sauvegarder le statut initial du serveur pour détecter les tests
    // (si le serveur indique "available" mais qu'on reçoit compteur_banc → c'est un test)
    this._serverInitialStatus = this.statusValue

    // S'abonner au channel partagé avec notre callback
    operationalStatusChannel.subscribe(this.cabineIdValue, (data) => this.handleUpdate(data))

    // Indicateur de connexion
    this.updateConnectionIndicator(true)

    // Timer local pour affichage fluide
    if (this.remainingSecondsValue > 0) {
      this.startLocalTimer()
    }

    // Si le statut est "loading", récupérer le statut initial via Rails
    // après un court délai pour laisser le temps au proxy de se connecter
    if (this.statusValue === 'loading') {
      this.loadingTimeout = setTimeout(() => {
        this.fetchInitialStatus()
      }, 2000)
    }
  }

  disconnect() {
    // Se désabonner du channel partagé
    operationalStatusChannel.unsubscribe(this.cabineIdValue)
    this.stopLocalTimer()
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout)
    }
  }

  // ===============================
  // Récupération du statut initial
  // ===============================

  async fetchInitialStatus() {
    // Si on a déjà reçu un statut via Action Cable, ne rien faire
    if (this.statusValue !== 'loading') return

    try {
      const response = await fetch(`/sunbeds/operational_status/cabines/${this.cabineIdValue}/status`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })

      if (response.ok) {
        const data = await response.json()
        this.statusValue = data.status || 'available'
        this.remainingSecondsValue = data.remaining_seconds || 0
        this.updateDisplay()
        
        if (this.remainingSecondsValue > 0) {
          this.startLocalTimer()
        }
      } else {
        // Si erreur, considérer comme disponible par défaut
        this.statusValue = 'available'
        this.updateDisplay()
      }
    } catch (error) {
      console.error('[OperationalStatus] Erreur fetch initial status:', error)
      // En cas d'erreur, considérer comme disponible par défaut
      this.statusValue = 'available'
      this.updateDisplay()
    }
  }

  // ===============================
  // Handler Action Cable
  // ===============================

  handleUpdate(data) {
    console.log('[OperationalStatus] handleUpdate() appelé avec type:', data.type)
    
    // Les données sont déjà filtrées par cabine_id dans le channel manager
    switch (data.type) {
      case "cabine_update":
        // Remplacement complet de la carte (infos client, RDV, alertes)
        console.log('[OperationalStatus] Remplacement de la carte par outerHTML')
        console.log('[OperationalStatus] HTML reçu (premiers 200 chars):', data.html?.substring(0, 200))
        this.element.outerHTML = data.html
        console.log('[OperationalStatus] Carte remplacée')
        break

      case "status_update":
        this.handleStatusUpdate(data)
        break

      case "preparation_updated":
        // Mise à jour de la durée de préparation depuis une autre fenêtre
        console.log('[OperationalStatus] preparation_updated reçu:', data)
        this.handlePreparationUpdated(data)
        break
    }
  }

  handlePreparationUpdated(data) {
    // Mettre à jour le temps restant
    this.remainingSecondsValue = data.remaining_seconds || 0

    // Mettre à jour l'affichage de la durée de préparation
    if (this.hasPrepDurationDisplayTarget) {
      this.prepDurationDisplayTarget.textContent = `${data.new_duration} min`
    }

    // Mettre à jour l'affichage du timer
    this.updateTimerDisplay()
    this.updateDisplay()

    // Redémarrer le timer local si du temps reste
    if (this.remainingSecondsValue > 0) {
      this.startLocalTimer()
    }
  }

  handleStatusUpdate(data) {
    const { event, data: payload } = data

    switch (event) {
      case "compteur_banc":
        // payload = seconds (number)
        this.remainingSecondsValue = payload
        
        // Détection automatique du mode test:
        // Si on reçoit compteur_banc mais qu'il n'y a pas de RDV actif → c'est un test
        // (un RDV est "actif" s'il est en preparing/in_progress/cooling_down/needs_cleaning)
        if (!this.hasActiveAppointment()) {
          this.isTestValue = true
          this.statusValue = 'testing'
        } else if (!this.isTestValue) {
          this.statusValue = 'in_progress'
        }
        this.updateDisplay()
        this.startLocalTimer()
        break

      case "vent_banc":
        // payload = seconds (number)
        console.log('[OperationalStatus] vent_banc reçu. Seconds:', payload, 'isTest:', this.isTestValue)
        this.remainingSecondsValue = payload
        if (payload > 0) {
          this.statusValue = 'cooling_down'
          this.startLocalTimer()
        } else {
          // Ventilation terminée: 
          // - Mode test → retourner à "available" et désactiver le mode test
          // - RDV actif (pas test) → passer à "needs_cleaning"
          if (this.isTestValue) {
            console.log('[OperationalStatus] Fin vent_banc test → available')
            this.isTestValue = false
            this.statusValue = 'available'
          } else if (this.hasActiveAppointment()) {
            console.log('[OperationalStatus] Fin vent_banc RDV → needs_cleaning')
            this.statusValue = 'needs_cleaning'
          } else {
            console.log('[OperationalStatus] Fin vent_banc → available')
            this.statusValue = 'available'
          }
        }
        this.updateDisplay()
        // Note: La synchronisation avec Rails se fait côté serveur
        // dans RaspberryPiWebsocketService.sync_appointment_status
        break

      case "etat":
        // payload = { status, remaining_seconds }
        this.statusValue = payload.status
        this.remainingSecondsValue = payload.remaining_seconds || 0
        this.updateDisplay()
        if (this.remainingSecondsValue > 0) {
          this.startLocalTimer()
        }
        break
    }
  }

  // Un RDV est "actif" s'il est dans un statut qui occupe le banc
  // (preparing, in_progress, cooling_down, needs_cleaning)
  // On vérifie si le RDV était déjà actif AVANT de recevoir les events du Raspberry Pi
  hasActiveAppointment() {
    if (!this.appointmentIdValue) return false
    // Un RDV prochain a le statut "available" côté serveur
    // Un RDV actif a un autre statut (preparing, in_progress, etc.)
    // On utilise le statut initial sauvegardé au connect()
    return this._serverInitialStatus && !['available', 'loading'].includes(this._serverInitialStatus)
  }

  updateConnectionIndicator(connected) {
    const indicator = this.element.querySelector('[data-connection-indicator]')
    if (indicator) {
      indicator.classList.toggle('bg-green-500', connected)
      indicator.classList.toggle('bg-gray-400', !connected)
    }
  }

  // ===============================
  // Timer et affichage
  // ===============================

  startLocalTimer() {
    this.stopLocalTimer()
    this.timerInterval = setInterval(() => {
      if (this.remainingSecondsValue > 0) {
        this.remainingSecondsValue--
        this.updateTimerDisplay()
        
        // Quand le timer atteint 0, gérer la transition de statut
        if (this.remainingSecondsValue === 0) {
          this.handleTimerComplete()
        }
      } else {
        this.stopLocalTimer()
      }
    }, 1000)
  }
  
  // Gère la fin du timer local (si l'event Raspberry n'est pas reçu)
  handleTimerComplete() {
    this.stopLocalTimer()
    
    console.log('[OperationalStatus] Timer terminé localement. Status actuel:', this.statusValue, 'isTest:', this.isTestValue)
    
    switch (this.statusValue) {
      case 'in_progress':
      case 'testing':
        // Fin de séance/test → passage en ventilation (sera mis à jour par vent_banc)
        // On ne change pas le statut ici, on attend l'event vent_banc du Raspberry
        break
        
      case 'cooling_down':
        // Fin de ventilation → transition vers le statut final
        if (this.isTestValue) {
          console.log('[OperationalStatus] Fin ventilation test → available')
          this.isTestValue = false
          this.statusValue = 'available'
        } else if (this.hasActiveAppointment()) {
          console.log('[OperationalStatus] Fin ventilation RDV → needs_cleaning')
          this.statusValue = 'needs_cleaning'
        } else {
          console.log('[OperationalStatus] Fin ventilation → available')
          this.statusValue = 'available'
        }
        this.updateDisplay()
        break
        
      case 'preparing':
        // Fin de préparation → in_progress (sera mis à jour par compteur_banc)
        break
    }
  }

  stopLocalTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval)
      this.timerInterval = null
    }
  }

  updateDisplay() {
    this.updateTimerDisplay()
    this.updateTimerLabel()
    this.updateStatusBadge()
    this.updateCardColor()
    this.updateTimerVisibility()
    this.updateActionButtons()
  }

  updateTimerDisplay() {
    if (!this.hasTimerTarget) return

    const minutes = Math.floor(this.remainingSecondsValue / 60)
    const seconds = this.remainingSecondsValue % 60
    this.timerTarget.textContent = `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  updateTimerLabel() {
    if (!this.hasTimerLabelTarget) return

    const labels = {
      preparing: 'Préparation',
      in_progress: 'Temps restant',
      testing: 'Test en cours',
      cooling_down: 'Ventilation'
    }
    this.timerLabelTarget.textContent = labels[this.statusValue] || 'Temps restant'
  }

  updateTimerVisibility() {
    if (!this.hasTimerContainerTarget) return

    const isActiveStatus =
      this.statusValue === 'preparing' ||
      this.statusValue === 'in_progress' ||
      this.statusValue === 'cooling_down' ||
      this.statusValue === 'testing'
    this.timerContainerTarget.classList.toggle('hidden', !isActiveStatus)
  }

  updateStatusBadge() {
    if (!this.hasStatusBadgeTarget) return

    const config = this.constructor.statusConfig[this.statusValue] || this.constructor.statusConfig.available

    // Reset classes
    this.statusBadgeTarget.className = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium'
    config.color.split(' ').forEach(c => this.statusBadgeTarget.classList.add(c))
    this.statusBadgeTarget.textContent = config.label
  }

  updateCardColor() {
    // Supprimer toutes les anciennes classes de couleur
    Object.values(this.constructor.cardColors).forEach(colorClasses => {
      colorClasses.split(' ').forEach(c => this.element.classList.remove(c))
    })
    
    // Ajouter la nouvelle couleur
    const newColor = this.constructor.cardColors[this.statusValue] || this.constructor.cardColors.available
    newColor.split(' ').forEach(c => this.element.classList.add(c))
  }

  updateActionButtons() {
    console.log('[OperationalStatus] updateActionButtons called. statusValue:', this.statusValue, 'cabineId:', this.cabineIdValue)
    
    // Bouton "Préparer" - visible uniquement si statut available et qu'il y a un appointment
    if (this.hasPrepareButtonTarget) {
      const showPrepare = this.statusValue === 'available' && this.appointmentIdValue
      this.prepareButtonTarget.classList.toggle('hidden', !showPrepare)
    }
    
    // Bouton "Nettoyage OK" - visible uniquement si statut needs_cleaning
    if (this.hasCleaningButtonTarget) {
      const showCleaning = this.statusValue === 'needs_cleaning'
      this.cleaningButtonTarget.classList.toggle('hidden', !showCleaning)
    }
    
    // Bouton "Test" - visible uniquement si statut available (cabine libre)
    if (this.hasTestButtonTarget) {
      const showTest = this.statusValue === 'available'
      this.testButtonTarget.classList.toggle('hidden', !showTest)
    }
    
    // Bouton "Stop Urgence" - visible si statut in_progress OU testing
    console.log('[OperationalStatus] Emergency stop button: hasTarget=', this.hasEmergencyStopButtonTarget, 'status=', this.statusValue)
    if (this.hasEmergencyStopButtonTarget) {
      const showEmergencyStop = this.statusValue === 'in_progress' || this.statusValue === 'testing'
      console.log('[OperationalStatus] Toggling emergencyStopButton. showEmergencyStop:', showEmergencyStop)
      this.emergencyStopButtonTarget.classList.toggle('hidden', !showEmergencyStop)
    }
  }

  // ===============================
  // Actions utilisateur
  // ===============================

  // Ajuster la durée de préparation (+/- minutes) pendant la phase de préparation
  async adjustPrepDuration(event) {
    const delta = parseInt(event.currentTarget.dataset.delta, 10)
    if (!this.appointmentIdValue || isNaN(delta)) return

    // Feedback visuel immédiat
    const button = event.currentTarget
    button.disabled = true
    button.classList.add('opacity-50')

    try {
      const response = await fetch('/sunbeds/operational_status/adjust_preparation_duration', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ appointment_id: this.appointmentIdValue, delta })
      })

      const data = await response.json()

      if (response.ok) {
        // Mettre à jour l'affichage de la durée de préparation
        if (this.hasPrepDurationDisplayTarget) {
          this.prepDurationDisplayTarget.textContent = `${data.new_duration} min`
        }
        // Utiliser la valeur serveur (plus précise que l'estimation locale)
        this.remainingSecondsValue = data.remaining_seconds ?? Math.max(0, this.remainingSecondsValue + (delta * 60))
        this.updateTimerDisplay()
        this.updateDisplay()

        // Redémarrer le timer local si du temps reste
        if (this.remainingSecondsValue > 0) {
          this.startLocalTimer()
        }
      } else {
        alert(data.error || 'Erreur lors de la modification')
      }
    } catch (error) {
      console.error('[OperationalStatus] Erreur adjustPrepDuration:', error)
      alert('Erreur de connexion')
    } finally {
      button.disabled = false
      button.classList.remove('opacity-50')
    }
  }

  // Confirmer nettoyage via Action Cable -> Raspberry Pi
  // Et appeler Rails pour mettre à jour le statut de l'appointment
  async confirmCleaning() {
    // Envoyer la commande au Raspberry Pi
    operationalStatusChannel.sendCommand(this.cabineIdValue, 'nettoyage', `x|${this.relayPinValue}`)
    
    // Appeler Rails pour mettre à jour l'appointment
    if (this.appointmentIdValue) {
      try {
        const response = await fetch(`/appointments/${this.appointmentIdValue}/confirm_cleaning`, {
          method: 'PATCH',
          headers: {
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
            'Accept': 'application/json'
          }
        })
        
        if (!response.ok) {
          console.error('[OperationalStatus] Erreur confirm_cleaning')
        }
      } catch (error) {
        console.error('[OperationalStatus] Erreur confirm_cleaning:', error)
      }
    }
    
    // Mise à jour locale
    this.statusValue = 'available'
    this.updateDisplay()
  }

  // Préparer la session (appel Rails via Turbo)
  async prepareSession(event) {
    const url = event.currentTarget.dataset.url
    if (!url) return
    
    try {
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
          'Accept': 'application/json'  // Ne pas demander turbo_stream pour éviter la modal
        }
      })
      
      if (response.ok) {
        // Ne pas traiter la réponse - la mise à jour sera faite via Action Cable broadcast
        this.statusValue = 'preparing'
        this.updateDisplay()
      } else {
        console.error('Erreur lors de la préparation de la session')
      }
    } catch (error) {
      console.error('Erreur:', error)
    }
  }

  // Ouvrir la modale de test
  openTestModal() {
    const modal = document.createElement('div')
    modal.id = 'test-sunbed-modal'
    modal.className = 'fixed inset-0 bg-black/50 flex items-center justify-center z-50'
    modal.innerHTML = `
      <div class="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
        <h3 class="text-lg font-bold text-gray-900 mb-4">Tester le banc</h3>
        <p class="text-sm text-gray-600 mb-4">Durée du test :</p>
        <div class="grid grid-cols-2 gap-2 mb-4">
          <button type="button" class="px-4 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 font-medium" data-duration="60">1 min</button>
          <button type="button" class="px-4 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 font-medium" data-duration="300">5 min</button>
          <button type="button" class="px-4 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 font-medium" data-duration="900">15 min</button>
          <button type="button" class="px-4 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 font-medium" data-duration="1200">20 min</button>
        </div>
        <button type="button" class="w-full px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg" data-close>Annuler</button>
      </div>
    `

    modal.querySelectorAll('[data-duration]').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const duration = parseInt(e.currentTarget.dataset.duration)
        modal.remove()
        await this.runTest(duration)
      })
    })

    modal.querySelector('[data-close]').addEventListener('click', () => {
      modal.remove()
    })

    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        modal.remove()
      }
    })

    document.body.appendChild(modal)
  }

  async runTest(durationSeconds) {
    try {
      const response = await fetch(`/sunbeds/operational_status/cabines/${this.cabineIdValue}/test_sunbed`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
          'Accept': 'text/vnd.turbo-stream.html'
        },
        body: JSON.stringify({ duration_seconds: durationSeconds })
      })
      
      if (response.ok) {
        console.log(`Test lancé pour ${durationSeconds} secondes`)
        // Activer le mode test
        this.isTestValue = true
        this.statusValue = 'testing'
        this.remainingSecondsValue = durationSeconds
        this.updateDisplay()
        this.startLocalTimer()
      } else {
        const contentType = response.headers.get('content-type')
        if (contentType && contentType.includes('text/vnd.turbo-stream.html')) {
          const html = await response.text()
          Turbo.renderStreamMessage(html)
        } else {
          console.error('Erreur lors du lancement du test')
        }
      }
    } catch (error) {
      console.error('Erreur:', error)
    }
  }
}
